import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {Option} from '@Components/contextual-menu/components/option';
import type {SliderOptionsRange} from '@Components/slider-options';
import {SliderOptions} from '@Components/slider-options';
import {type InputFieldControls, InputFieldSize} from '@Components/input-field-v2/input-field.types';
import styles from './contextual-menu-slider-options.module.scss';

interface ContextualMenuSliderOptionsProps {
  id: string;
  className?: string;
  inputCounterClassName?: string;
  value: number;
  label: string;
  range: SliderOptionsRange;
  transformDisplay?: SliderOptionsRange;
  marks?: number[];
  useMarksAsSteps?: boolean;
  step?: number;
  minimumStepFromInputField?: number;
  precision?: number;
  isDisabled?: boolean;

  onChangeEnded?(value: number): void;

  onChange(value: number): void;

  wrapText?: boolean;
  addDelayToOnChange?: boolean;
  delayForOnChange?: number;
  controlIcons?: string[];
  hideSlider?: boolean;
  inputFieldControls?: InputFieldControls;
  inputFieldClassname?: string;
  overrideDefaultContols?: boolean;
  onClickControl?(controlIndex: number): void;
  controlsDisabledStates?: boolean[];
}

const DELAY_FOR_ON_CHANGE = 200;

export function ContextualMenuSliderOptions({
  marks = [],
  className = '',
  useMarksAsSteps = false,
  step = 1,
  minimumStepFromInputField = step,
  onChangeEnded = (): void => {},
  wrapText = true,
  isDisabled = false,
  addDelayToOnChange = false,
  delayForOnChange = DELAY_FOR_ON_CHANGE,
  hideSlider = false,
  inputCounterClassName = '',
  ...props
}: ContextualMenuSliderOptionsProps): ReactElement<any> {
  useEffect((): void => {
    if (addDelayToOnChange) {
      setTempVal(props.value);
    }
  }, [props.value]);

  const [tempVal, setTempVal] = useState(props.value);
  const timer = useRef<number | undefined>(undefined);

  const onChangeHandler = (value: number): void => {
    setTempVal(value);
    if (!addDelayToOnChange) {
      props.onChange(value);
      return;
    }

    if (timer) {
      clearTimeout(timer.current);
    }

    timer.current = window.setTimeout(() => {
      props.onChange(value);
    }, delayForOnChange);
  };

  const getSlider = (): ReactElement<any> => {
    return (
      <SliderOptions
        id={props.id}
        key={props.id}
        className="spacing-m-t-0"
        marks={marks}
        value={addDelayToOnChange ? tempVal : props.value}
        label={props.label}
        range={props.range}
        transformDisplayTo={props.transformDisplay}
        onChange={onChangeHandler}
        onChangeEnded={onChangeEnded}
        useMarksAsSteps={useMarksAsSteps}
        step={step}
        minimumStepFromInputField={minimumStepFromInputField}
        inputCounterClassName={inputCounterClassName !== '' ? inputCounterClassName : styles.sliderOptions}
        wrapText={wrapText}
        isDisabled={isDisabled}
        precision={props.precision}
        size={InputFieldSize.XSMALL}
        controlIcons={props.controlIcons}
        hideSlider={hideSlider}
        inputFieldControls={props.inputFieldControls}
        inputFieldClassname={props.inputFieldClassname}
        controlsDisabledStates={props.controlsDisabledStates}
        overrideDefaultContols={props.overrideDefaultContols}
        onClickControl={(index) => {
          if (props.onClickControl) {
            props.onClickControl(index);
          }
        }}
      />
    );
  };
  return <Option className={className} optionControl={getSlider()} />;
}
